// @ts-nocheck
import { useState, useEffect, useRef } from 'react'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { avatarColor, avatarInitial } from '../lib/avatar'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { useDocumentFormatting } from '../hooks/useDocumentFormatting'
import { useAutoSave } from '../hooks/useAutoSave'
import { useReformat } from '../hooks/useReformat'
import { Layout } from '../components/layout/Layout'
import { FolderSelector } from '../components/folders/FolderSelector'
import { MarkdownEditor } from '../components/editor/MarkdownEditor'
import { RichTextEditor } from '../components/editor/RichTextEditor'
import { MarkdownRenderer } from '../components/editor/MarkdownRenderer'
import { useFolders } from '../hooks/useFolders'
import { MemberSearchAdd, MemberAvatar } from '../components/sharing/MemberSearchAdd'
import { ActionItemTaskify } from '../components/documents/ActionItemTaskify'
import { ExportButton } from '../components/documents/ExportButton'
import { RelatedMeetings } from '../components/documents/RelatedMeetings'
import { ref as storageRef, getDownloadURL } from 'firebase/storage'
import { storage } from '../lib/firebase'
import { AudioPlayerCard } from '../components/documents/AudioPlayerCard'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

/** mimeType または拡張子から動画メディアかどうかを判定 */
function isVideoMedia(mimeType?: string, filename?: string): boolean {
  if (mimeType?.startsWith('video/')) return true
  return /\.(mp4|mov|avi|mkv|webm|m4v)$/i.test(filename || '')
}

async function fetchDocument(documentId: string, user: any) {
  const token = await user.getIdToken()
  const tokenResult = await user.getIdTokenResult()
  const orgId = tokenResult.claims.orgId

  if (!orgId) {
    throw new Error('Organization ID not found in user claims')
  }

  const url = `${API_BASE}/v1/organizations/${orgId}/documents/${documentId}`

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`Document fetch failed: ${response.statusText}`)
  }

  const data = await response.json()
  console.log('📄 Document fetched:', {
    id: documentId,
    hasFormatted: !!data.formatted,
    formattedStatus: data.formatted?.status
  })
  return data
}

// ── 話者ターン表示（文字起こしタブの読み取り専用表示） ──
const SPEAKER_LINE_RE = /^(.{1,40}?): (.+)$/

interface TranscriptTurn {
  speaker: string | null
  text: string
}

/** 「話者名: 発言」形式の行を話者ターンにパースする */
function parseTranscriptTurns(text: string): TranscriptTurn[] {
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim())
    .map((line) => {
      const m = line.match(SPEAKER_LINE_RE)
      return m ? { speaker: m[1].trim(), text: m[2] } : { speaker: null, text: line }
    })
}

/** 話者ラベル付き文字起こしかどうか（2行以上の話者行がある場合） */
function hasSpeakerTurns(text: string): boolean {
  if (!text) return false
  return parseTranscriptTurns(text).filter((t) => t.speaker).length >= 2
}

function SpeakerTranscript({ text }: { text: string }) {
  const turns = parseTranscriptTurns(text)
  return (
    <div className="flex flex-col gap-3" style={{ padding: 'var(--space-2) 0' }}>
      {turns.map((turn, i) =>
        turn.speaker ? (
          <div key={i} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: avatarColor(turn.speaker)
                }}
              />
              <span className="text-sm font-semibold">{turn.speaker}</span>
            </div>
            <p className="text-sm" style={{ margin: 0, paddingLeft: 18, whiteSpace: 'pre-wrap' }}>
              {turn.text}
            </p>
          </div>
        ) : (
          <p key={i} className="text-sm" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            {turn.text}
          </p>
        )
      )}
    </div>
  )
}

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, firebaseUser } = useAuth()
  const [activeTab, setActiveTab] = useState<'transcribe' | 'format'>(
    searchParams.get('tab') === 'transcribe' ? 'transcribe' : 'format'
  )
  const [isEditing, setIsEditing] = useState(false)
  const [isEditingFormatted, setIsEditingFormatted] = useState(false)
  const [isTitleEditing, setIsTitleEditing] = useState(false)
  const [editedTitle, setEditedTitle] = useState('')
  const [isDateEditing, setIsDateEditing] = useState(false)
  const [editedDate, setEditedDate] = useState('')
  const [editedContent, setEditedContent] = useState('')
  const [editedFormattedContent, setEditedFormattedContent] = useState('')
  const [editedFolderId, setEditedFolderId] = useState<string | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [retryMessage, setRetryMessage] = useState<string | null>(null)
  const [formattingProgress, setFormattingProgress] = useState(0)
  const [formattingEstimatedTime, setFormattingEstimatedTime] = useState<string | null>(null)
  const [formattingStartTime, setFormattingStartTime] = useState<number | null>(null)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareUrl, setShareUrl] = useState('')
  const [shareLinkExpiry, setShareLinkExpiry] = useState<'none' | '7d' | '30d' | '90d'>('none')
  const [shareLinkPassword, setShareLinkPassword] = useState('')
  const [isCopied, setIsCopied] = useState(false)
  // 公開範囲（visibilityモデル）
  const [docVisibility, setDocVisibility] = useState<'private' | 'members' | 'folder' | 'org'>('private')
  const [aclEntries, setAclEntries] = useState<{ uid: string; email: string; displayName?: string; role: 'owner' | 'editor' | 'viewer' }[]>([])
  const [orgMembers, setOrgMembers] = useState<{ uid: string; email: string; displayName?: string }[]>([])
  const [isSavingShare, setIsSavingShare] = useState(false)
  const [isLinkBusy, setIsLinkBusy] = useState(false)
  const [originalTranscriptText, setOriginalTranscriptText] = useState('')
  const [currentTranscriptText, setCurrentTranscriptText] = useState('')
  const [originalFormattedText, setOriginalFormattedText] = useState('')
  const [currentFormattedText, setCurrentFormattedText] = useState('')
  const [isFormattedEditing, setIsFormattedEditing] = useState(false)
  const [markdownCopied, setMarkdownCopied] = useState(false)
  const [transcriptCopied, setTranscriptCopied] = useState(false)
  const [isFolderEditing, setIsFolderEditing] = useState(false)
  const [tempFolderId, setTempFolderId] = useState<string | null>(null)
  const folderDropdownRef = useRef<HTMLDivElement>(null)
  const [recordingVideoUrl, setRecordingVideoUrl] = useState<string | null>(null)
  const [recordingUrlError, setRecordingUrlError] = useState<string | null>(null)
  // 文字起こしタブの表示モード（話者ターン表示 / テキスト編集）
  const [transcriptViewMode, setTranscriptViewMode] = useState<'speakers' | 'edit'>('speakers')
  // 参加者カードの「関連するやりとり」（最初の外部参加者のattendee-context）
  const [participantContext, setParticipantContext] = useState<any>(null)
  const [participantContextLoading, setParticipantContextLoading] = useState(false)
  
  const { formatDocument, isLoading: isFormattingAPI, error: formatError } = useDocumentFormatting()
  const queryClient = useQueryClient()
  const { data: foldersData } = useFolders()
  const folders = foldersData?.folders || []

  // Document query - moved before the functions that use it
  const { data: documentData, isLoading, error } = useQuery({
    queryKey: ['document', id],
    queryFn: async () => {
      if (!firebaseUser || !id) throw new Error('Missing authentication or document ID')
      return fetchDocument(id, firebaseUser)
    },
    enabled: !!user && !!firebaseUser && !!id,
    staleTime: 30 * 1000, // 30秒間キャッシュを新鮮とみなす（更新後はinvalidateQueriesで反映）
    refetchOnWindowFocus: false
  })

  // AI整形の状態管理
  // 注: 整形がタイムアウトで強制終了すると formatted.status が 'processing' のまま残るため、
  // 更新から5分以上経過した 'processing' は「固まった」とみなして再実行を許可する（自己回復）。
  const isStaleProcessing = (() => {
    if (documentData?.formatted?.status !== 'processing') return false
    const updatedRaw = documentData?.audit?.updatedAt
    const updatedMs = updatedRaw?._seconds
      ? updatedRaw._seconds * 1000
      : (updatedRaw ? new Date(updatedRaw).getTime() : 0)
    return !updatedMs || Date.now() - updatedMs > 5 * 60 * 1000
  })()
  const isFormatting = isFormattingAPI || (documentData?.formatted?.status === 'processing' && !isStaleProcessing)

  // 自動保存フック（文字起こし用）
  const transcriptAutoSave = useAutoSave({
    documentId: id || '',
    onSave: async (content: string) => {
      if (!firebaseUser || !id) return
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transcript: {
            text: content,
            source: 'manual_edit',
            lang: 'ja-JP'
          }
        })
      })
      if (!response.ok) {
        throw new Error('文字起こしの保存に失敗しました')
      }
    },
    enabled: !!id && !!firebaseUser
  })

  // 自動保存フック（整形済み議事録用）
  const formattedAutoSave = useAutoSave({
    documentId: id || '',
    onSave: async (content: string) => {
      if (!firebaseUser || !id) return
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formatted: {
            markdown: content,
            status: 'completed',
            version: Date.now()
          }
        })
      })
      if (!response.ok) {
        throw new Error('議事録の保存に失敗しました')
      }
    },
    enabled: !!id && !!firebaseUser
  })

  // 再成形フック
  const { reformat, isReformatting, reformatError } = useReformat({
    documentId: id || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] })
      setOriginalTranscriptText(currentTranscriptText)
    },
    onError: (error) => {
      console.error('Reformat error:', error)
    }
  })

  // タイトル専用の保存関数
  const handleSaveTitle = async (newTitle: string) => {
    if (!id || !firebaseUser || !documentData || newTitle.trim() === documentData.title.trim()) {
      return
    }

    try {
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newTitle.trim()
        })
      })

      if (response.ok) {
        // クエリキャッシュを更新
        queryClient.invalidateQueries({ queryKey: ['document', id] })
        queryClient.invalidateQueries({ queryKey: ['documents'] })
        console.log('✅ Title saved successfully')
      } else {
        console.error('❌ Title save failed:', response.statusText)
      }
    } catch (error) {
      console.error('❌ Title save error:', error)
    }
  }

  // 文字起こし機能
  const handleFileUpload = async (file: File) => {
    if (!id || !firebaseUser) return

    setIsTranscribing(true)
    setTranscriptionError(null)

    try {
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      // まず Firebase Storage に直接アップロードし、その後にパスを渡して文字起こしを依頼する
      // （バックエンドはmultipartではなく { audioPath } を期待している）
      const storagePath = `audio/${orgId}/${id}/${Date.now()}_${file.name}`
      const { uploadBytes } = await import('firebase/storage')
      await uploadBytes(storageRef(storage, storagePath), file)

      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          audioPath: storagePath,
          fileName: file.name,
          mimeType: file.type
        })
      })

      if (response.ok) {
        const result = await response.json()
        console.log('✅ Transcription started:', result)
        
        // ドキュメントを再取得してUIを更新
        queryClient.invalidateQueries({ queryKey: ['document', id] })
        
        setSaveMessage('文字起こしを開始しました。処理には数分かかる場合があります。')
        setTimeout(() => setSaveMessage(null), 5000)
      } else {
        const errorText = await response.text()
        throw new Error(`Transcription failed: ${response.status} - ${errorText}`)
      }
    } catch (error) {
      console.error('❌ Transcription error:', error)
      setTranscriptionError(error instanceof Error ? error.message : '文字起こしに失敗しました')
    } finally {
      setIsTranscribing(false)
    }
  }

  const handleFileSelect = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'audio/*,video/*,.mp3,.wav,.m4a,.mp4,.mov,.avi'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        handleFileUpload(file)
      }
    }
    input.click()
  }

  // botの録画音声から文字起こしをやり直す（MeetingBaaS文字起こし失敗時のリカバリ）
  const [isRetranscribing, setIsRetranscribing] = useState(false)
  const handleRetranscribeBot = async () => {
    const botId = documentData?.integration?.externalId
    if (!id || !firebaseUser || !botId) return
    setIsRetranscribing(true)
    setTranscriptionError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/meeting-bots/jobs/${botId}/retranscribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      })
      if (!response.ok) {
        const t = await response.text()
        throw new Error(`再文字起こしに失敗しました (${response.status}) - ${t}`)
      }
      const result = await response.json()
      queryClient.invalidateQueries({ queryKey: ['document', id] })
      setSaveMessage(`文字起こしが完了しました（${result.transcriptLength ?? 0}文字）。AI整形も自動実行されます。`)
      setTimeout(() => setSaveMessage(null), 6000)
    } catch (error) {
      console.error('❌ Retranscribe error:', error)
      setTranscriptionError(error instanceof Error ? error.message : '再文字起こしに失敗しました')
    } finally {
      setIsRetranscribing(false)
    }
  }

  // 録画動画の再生URLを取得（Firebase Storage クライアントSDK経由・要認証）
  useEffect(() => {
    const path = documentData?.recording?.path
    if (!path) {
      setRecordingVideoUrl(null)
      return
    }
    let cancelled = false
    setRecordingUrlError(null)
    getDownloadURL(storageRef(storage, path))
      .then((url) => {
        if (!cancelled) setRecordingVideoUrl(url)
      })
      .catch((err) => {
        console.error('❌ Recording URL fetch failed:', err)
        if (!cancelled) setRecordingUrlError('録画の読み込みに失敗しました')
      })
    return () => { cancelled = true }
  }, [documentData?.recording?.path])

  // 最初の外部参加者（メールアドレス形式・自分以外）のコンテキストを取得
  // 参加者リストのクリーンアップ（既存ドキュメント対策）:
  // bot自身を除外し、片方がもう片方に含まれる表記揺れ・大文字小文字違いの重複を統合
  const participants: string[] = (() => {
    const raw: string[] = Array.isArray(documentData?.participants) ? documentData.participants : []
    const unique: string[] = []
    for (const r of raw) {
      const name = String(r || '').trim()
      if (!name || /ボット|\bbot\b/i.test(name)) continue
      const lower = name.toLowerCase()
      const dup = unique.findIndex(u => {
        const ul = u.toLowerCase()
        return ul === lower || ul.includes(lower) || lower.includes(ul)
      })
      if (dup === -1) unique.push(name)
      else if (name.length > unique[dup].length) unique[dup] = name
    }
    return unique
  })()

  // 参加者の編集（追加・削除）
  const [isEditingParticipants, setIsEditingParticipants] = useState(false)
  const [newParticipantName, setNewParticipantName] = useState('')
  const saveParticipants = async (next: string[]) => {
    if (!id || !firebaseUser) return
    try {
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const res = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ participants: next })
      })
      if (!res.ok) throw new Error(`参加者の更新に失敗しました (${res.status})`)
      queryClient.invalidateQueries({ queryKey: ['document', id] })
    } catch (e) {
      console.error('participants update failed:', e)
      setSaveMessage('参加者の更新に失敗しました')
      setTimeout(() => setSaveMessage(null), 4000)
    }
  }
  const removeParticipant = (name: string) => saveParticipants(participants.filter(p => p !== name))
  const addParticipant = () => {
    const name = newParticipantName.trim()
    if (!name || participants.includes(name)) return
    setNewParticipantName('')
    saveParticipants([...participants, name])
  }
  const firstExternalEmail = participants.find(
    (p) => typeof p === 'string' && p.includes('@') && p.toLowerCase() !== (user?.email || '').toLowerCase()
  ) || null
  useEffect(() => {
    if (!firstExternalEmail || !firebaseUser) {
      setParticipantContext(null)
      return
    }
    let cancelled = false
    setParticipantContextLoading(true)
    ;(async () => {
      try {
        const token = await firebaseUser.getIdToken()
        const res = await fetch(
          `${API_BASE}/v1/meeting-bots/attendee-context?email=${encodeURIComponent(firstExternalEmail)}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        )
        const data = await res.json().catch(() => null)
        if (!cancelled && res.ok && data) setParticipantContext(data)
      } catch {
        // ベストエフォート
      } finally {
        if (!cancelled) setParticipantContextLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [firstExternalEmail, firebaseUser])

  // URLパラメータからタブを設定
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab && ['transcribe', 'format'].includes(tab)) {
      setActiveTab(tab as any)
    } else {
      // URLパラメータがない場合はAI整形タブをデフォルトに
      setActiveTab('format')
    }
  }, [searchParams])

  // 外部クリックでフォルダドロップダウンを閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (folderDropdownRef.current && !folderDropdownRef.current.contains(event.target as Node)) {
        setIsFolderEditing(false)
      }
    }

    if (isFolderEditing) {
      window.document.addEventListener('mousedown', handleClickOutside)
      return () => window.document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isFolderEditing])


  const handleSaveDocument = async () => {
    if (!id || !firebaseUser) {
      console.error('❌ Save failed: Missing id or firebaseUser', { id, firebaseUser: !!firebaseUser })
      return
    }

    if (!documentData) {
      console.error('❌ Save failed: Document not loaded')
      setSaveMessage('ドキュメントが読み込まれていません')
      return
    }

    try {
      setIsSaving(true)
      setSaveMessage(null)
      
      console.log('💾 Saving document:', { id, hasContent: !!editedContent })

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: editedTitle,
          transcript: {
            text: editedContent
          },
          folderId: editedFolderId,
          ...(editedFormattedContent && documentData?.formatted && {
            formatted: {
              ...documentData.formatted,
              markdown: editedFormattedContent
            }
          })
        })
      })

      if (!response.ok) {
        const errorData = await response.text()
        console.error('❌ Save failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        })
        throw new Error(`Save failed: ${response.status} ${response.statusText} - ${errorData}`)
      }

      const updatedDocument = await response.json()

      // キャッシュを更新
      queryClient.setQueryData(['document', id], updatedDocument)

      setSaveMessage('保存しました')
      setTimeout(() => setSaveMessage(null), 3000)

      console.log('✅ Document saved successfully')

    } catch (error) {
      console.error('❌ Save failed:', error)
      setSaveMessage('保存に失敗しました: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleFormatDocument = async (forceRegenerate: boolean = false, retryCount: number = 0) => {
    if (!id || !documentData) return

    // 文字起こしデータがない場合は警告
    if (!documentData.transcript || !documentData.transcript.text) {
      setRetryMessage('文字起こしデータがありません。まず音声ファイルをアップロードして文字起こしを実行してください。')
      setTimeout(() => setRetryMessage(null), 5000)
      return
    }

    // 処理時間の目安を計算（文字数ベース）
    const transcriptLength = documentData.transcript?.text?.length || 0
    const estimatedMinutes = Math.ceil(transcriptLength / 30000) // 30,000文字あたり1分
    const estimatedEndTime = new Date(Date.now() + estimatedMinutes * 60 * 1000)
    const endTimeStr = `${estimatedEndTime.getHours()}:${estimatedEndTime.getMinutes().toString().padStart(2, '0')}`
    
    // 処理開始
    setFormattingStartTime(Date.now())
    setFormattingProgress(0)
    setFormattingEstimatedTime(endTimeStr)
    setRetryMessage(`🚀 AI整形を開始しました！推定完了時刻: ${endTimeStr} (約${estimatedMinutes}分)`)
    
    // 進捗バーのシミュレーション
    const progressInterval = setInterval(() => {
      setFormattingProgress(prev => {
        if (prev >= 90) return prev
        return prev + 10
      })
    }, (estimatedMinutes * 60 * 1000) / 9) // 90%まで均等に進める
    
    // formatErrorもクリアして、前回のエラー表示を消去
    if (formatError) {
      // useDocumentFormattingのerrorをクリアするため、新しい処理を開始
      console.log('🧹 Clearing previous format error state')
    }

    try {
      console.log('🔄 Starting AI formatting for document:', id, 'forceRegenerate:', forceRegenerate, 'retry:', retryCount)
      console.log('📋 Document data:', {
        hasTranscript: !!documentData.transcript,
        transcriptLength: transcriptLength,
        estimatedMinutes: estimatedMinutes,
        documentId: id
      })
      const result = await formatDocument(id, { forceRegenerate })
      console.log('✅ Formatting API result:', result)
      
      // 進捗を100%に
      clearInterval(progressInterval)
      setFormattingProgress(100)

      // キャッシュを強制的に無効化
      console.log('🗑️ Invalidating cache...')
      await queryClient.invalidateQueries({ queryKey: ['document', id] })

      // キャッシュバスティングで強制的に最新データを取得
      console.log('🔄 Fetching fresh document data...')
      const freshData = await fetchDocument(id, firebaseUser)
      console.log('✅ Fresh document data:', freshData)

      // React Queryのキャッシュも更新
      queryClient.setQueryData(['document', id], freshData)

      // formatタブに切り替えて結果を表示
      setActiveTab('format')
      console.log('📊 Switched to format tab')

      // 処理完了メッセージを表示してからリロード
      setRetryMessage('✅ AI整形が完了しました！')
      setTimeout(() => {
        setRetryMessage(null)
        setFormattingProgress(0)
        setFormattingEstimatedTime(null)
        setFormattingStartTime(null)
      }, 3000)

    } catch (error) {
      clearInterval(progressInterval)
      setFormattingProgress(0)
      setFormattingEstimatedTime(null)
      setFormattingStartTime(null)
      console.error('❌ Formatting failed:', error)
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error
      })
      const errorMessage = error instanceof Error ? error.message : 'AI整形に失敗しました'

      // レート制限エラーの場合は自動リトライ
      if (errorMessage.includes('AI サービスが混雑しています') && retryCount < 3) {
        const waitTime = Math.pow(2, retryCount) * 2000 // 2秒, 4秒, 8秒の指数バックオフ
        console.log(`⏰ Rate limited, retrying in ${waitTime/1000} seconds... (attempt ${retryCount + 1}/3)`)

        setRetryMessage(`AI サービスが混雑しています。${waitTime/1000}秒後に自動で再試行します... (${retryCount + 1}/3)`)

        setTimeout(() => {
          setRetryMessage(null)
          handleFormatDocument(forceRegenerate, retryCount + 1)
        }, waitTime)
      } else {
        setRetryMessage(errorMessage)
      }
    }
  }

  // ドキュメントが読み込まれたら編集用コンテンツを初期化
  useEffect(() => {
    if (documentData) {
      setEditedTitle(documentData.title || '')
      setEditedContent(documentData.transcript?.text || '')
      setEditedFormattedContent(documentData.formatted?.markdown || '')
      setEditedFolderId(documentData.folderId || null)
      
      // 自動保存用の初期値を設定
      const transcriptText = documentData.transcript?.text || ''
      const formattedText = documentData.formatted?.markdown || ''
      setOriginalTranscriptText(transcriptText)
      setCurrentTranscriptText(transcriptText)
      setOriginalFormattedText(formattedText)
      setCurrentFormattedText(formattedText)
      
      // フォルダ情報を設定
      setTempFolderId(documentData.folderId || null)
      
      // 公開範囲を設定（visibility未設定の既存ドキュメントは従来の実効範囲に合わせる:
      // フォルダあり → フォルダ内、なし → 自分だけ）
      const v = (documentData as any).visibility
      setDocVisibility(
        v === 'private' || v === 'members' || v === 'folder' || v === 'org'
          ? v
          : (documentData.folderId ? 'folder' : 'private')
      )
    }
  }, [documentData])

  // 共有ダイアログを開いたら組織メンバーとACLリストを読み込む
  useEffect(() => {
    const loadShareData = async () => {
      if (!showShareDialog || !firebaseUser || !user?.orgId) return
      try {
        const token = await firebaseUser.getIdToken()
        const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/members`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
        if (!response.ok) throw new Error('Failed to load members')
        const data = await response.json()
        const members = (data.members || []).map((m: any) => ({
          uid: m.uid,
          email: m.email,
          displayName: m.displayName || m.email
        }))
        setOrgMembers(members)

        // 現在のACLからアクセスリストを構築
        const acl = (documentData as any)?.acl || {}
        const entries: { uid: string; email: string; displayName?: string; role: 'owner' | 'editor' | 'viewer' }[] = []
        const pushRole = (uids: string[] | undefined, role: 'owner' | 'editor' | 'viewer') => {
          for (const uid of uids || []) {
            if (entries.some(e => e.uid === uid)) continue
            const m = members.find((om: any) => om.uid === uid)
            entries.push({ uid, email: m?.email || uid, displayName: m?.displayName || m?.email || uid, role })
          }
        }
        pushRole(acl.owners, 'owner')
        pushRole(acl.editors, 'editor')
        pushRole(acl.viewers, 'viewer')
        setAclEntries(entries)

        // 既存の共有リンク（public）があればURLを表示
        try {
          const sharesResponse = await fetch(`${API_BASE}/v1/shares?resourceType=document&resourceId=${id}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          })
          if (sharesResponse.ok) {
            const sharesData = await sharesResponse.json()
            const publicShare = (sharesData.shares || []).find((s: any) =>
              s.recipients?.some((r: any) => r.type === 'public')
            )
            setShareUrl(publicShare ? `${window.location.origin}/share/${publicShare.id}` : '')
          }
        } catch (shareError) {
          console.warn('Failed to load existing shares:', shareError)
        }
      } catch (error) {
        console.error('Failed to load share data:', error)
      }
    }
    loadShareData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showShareDialog])

  // Escキーでダイアログを閉じる
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showShareDialog) {
          setShowShareDialog(false)
          setIsCopied(false)
        }
        if (showDeleteDialog) {
          setShowDeleteDialog(false)
        }
      }
    }

    window.addEventListener('keydown', handleEscKey)
    return () => {
      window.removeEventListener('keydown', handleEscKey)
    }
  }, [showShareDialog, showDeleteDialog])

  const handleFolderSelect = (folderId: string | null) => {
    setSelectedFolderId(folderId)
    // フォルダが選択された場合は一覧ページに遷移
    if (folderId) {
      navigate(`/documents?folder=${folderId}`)
    } else {
      navigate('/documents')
    }
  }

  // Notionライクな自動保存機能
  const handleFolderAutoSave = async (newFolderId: string | null) => {
    if (!id || !firebaseUser) return

    try {
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          folderId: newFolderId
        })
      })

      if (!response.ok) {
        throw new Error(`Folder update failed: ${response.statusText}`)
      }

      // キャッシュを更新
      queryClient.invalidateQueries({ queryKey: ['document', id] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      
      setIsFolderEditing(false)
      console.log('✅ Folder updated successfully:', newFolderId)

    } catch (error) {
      console.error('❌ Folder update failed:', error)
      alert('フォルダの更新に失敗しました: ' + (error instanceof Error ? error.message : 'Unknown error'))
    }
  }

  // フォルダ保存機能（従来版 - 削除予定）
  const handleSaveFolder = async () => {
    if (!id || !firebaseUser || tempFolderId === documentData?.folderId) {
      setIsFolderEditing(false)
      return
    }

    try {
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          folderId: tempFolderId
        })
      })

      if (!response.ok) {
        throw new Error(`Folder update failed: ${response.statusText}`)
      }

      // キャッシュを更新
      queryClient.invalidateQueries({ queryKey: ['document', id] })
      
      setIsFolderEditing(false)
      console.log('✅ Folder updated successfully:', tempFolderId)

    } catch (error) {
      console.error('❌ Folder update failed:', error)
      // エラー時のみ通知を表示
      alert('フォルダの更新に失敗しました: ' + (error instanceof Error ? error.message : 'Unknown error'))
    }
  }

  const handleCancelFolderEdit = () => {
    setTempFolderId(documentData?.folderId || null)
    setIsFolderEditing(false)
  }

  const handleFolderCreate = () => {
    // フォルダ作成後の処理（必要に応じて実装）
  }

  // 公開範囲とメンバーリストを保存
  const handleSaveVisibility = async () => {
    if (!id || !firebaseUser || !documentData) return

    setIsSavingShare(true)
    try {
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          visibility: docVisibility,
          acl: {
            owners: aclEntries.filter(e => e.role === 'owner').map(e => e.uid),
            editors: aclEntries.filter(e => e.role === 'editor').map(e => e.uid),
            viewers: aclEntries.filter(e => e.role === 'viewer').map(e => e.uid)
          }
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || error.error || '公開範囲の更新に失敗しました')
      }

      queryClient.invalidateQueries({ queryKey: ['document', id] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      setShowShareDialog(false)
    } catch (error) {
      console.error('Failed to update visibility:', error)
      alert(error instanceof Error ? error.message : '公開範囲の更新に失敗しました')
    } finally {
      setIsSavingShare(false)
    }
  }

  // ブックマークのトグル（フォルダ横のボタン）
  const isBookmarked = !!(documentData as any)?.bookmarks?.[firebaseUser?.uid || '']
  const handleToggleBookmark = async () => {
    if (!id || !firebaseUser) return
    try {
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const res = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}/bookmark`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookmarked: !isBookmarked })
      })
      if (!res.ok) throw new Error(`${res.status}`)
      queryClient.invalidateQueries({ queryKey: ['document', id] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    } catch (error) {
      console.error('Bookmark toggle failed:', error)
      alert('ブックマークの更新に失敗しました')
    }
  }

  // リンク共有: URLを知っている人が閲覧できる共有リンクを作成
  const handleCreateShareLink = async () => {
    if (!id || !firebaseUser) return

    setIsLinkBusy(true)
    try {
      const token = await firebaseUser.getIdToken()
      const expiryDays = { none: 0, '7d': 7, '30d': 30, '90d': 90 }[shareLinkExpiry]
      const expiresAt = expiryDays > 0
        ? new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toISOString()
        : null
      const password = shareLinkPassword.trim()
      const shareRequest = {
        resourceType: 'document',
        resourceId: id,
        permissions: [{ action: 'view' as const, granted: true }],
        recipients: [{ type: 'public' as const, identifier: 'public' }],
        expiresAt,
        settings: {
          allowDownload: true,
          allowComments: false,
          requireAuth: false,
          passwordProtected: !!password,
          ...(password ? { password } : {})
        }
      }

      const response = await fetch(`${API_BASE}/v1/shares`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(shareRequest)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || error.error || '共有リンクの作成に失敗しました')
      }

      const result = await response.json()
      setShareLinkPassword('')
      if (result.shareUrl) {
        setShareUrl(result.shareUrl)
      } else if (result.share?.id) {
        setShareUrl(`${window.location.origin}/share/${result.share.id}`)
      }

      queryClient.invalidateQueries({ queryKey: ['document', id] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    } catch (error) {
      console.error('Failed to create share link:', error)
      alert(error instanceof Error ? error.message : '共有リンクの作成に失敗しました')
    } finally {
      setIsLinkBusy(false)
    }
  }

  // リンク共有を解除（このドキュメントの共有リンクをすべて削除）
  const handleRevokeShareLinks = async () => {
    if (!id || !firebaseUser) return

    setIsLinkBusy(true)
    try {
      const token = await firebaseUser.getIdToken()
      const sharesResponse = await fetch(`${API_BASE}/v1/shares?resourceType=document&resourceId=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (sharesResponse.ok) {
        const sharesData = await sharesResponse.json()
        for (const share of sharesData.shares || []) {
          await fetch(`${API_BASE}/v1/shares/${share.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        }
      }

      setShareUrl('')
      queryClient.invalidateQueries({ queryKey: ['document', id] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    } catch (error) {
      console.error('Failed to remove shares:', error)
      alert('共有リンクの解除に失敗しました')
    } finally {
      setIsLinkBusy(false)
    }
  }

  const handleDeleteDocument = async () => {
    if (!id || !firebaseUser) return

    setIsDeleting(true)
    try {
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`)
      }

      // 成功時は一覧ページに戻る
      navigate('/documents')
    } catch (error) {
      console.error('❌ Delete failed:', error)
      alert('議事録の削除に失敗しました: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex-1 w-full flex items-center justify-center" style={{ background: 'var(--color-surface)', minHeight: '100vh' }}>
        <div className="flex flex-col items-center gap-3">
          <span className="spinner lg" />
          <p className="text-secondary">議事録を読み込み中...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 w-full flex items-center justify-center" style={{ background: 'var(--color-surface)', minHeight: '100vh' }}>
        <div className="empty-state">
          <div className="empty-state-title">議事録の読み込みに失敗しました</div>
          <button
            onClick={() => navigate('/documents')}
            className="btn btn-primary"
            style={{ marginTop: 'var(--space-4)' }}
          >
            一覧に戻る
          </button>
        </div>
      </div>
    )
  }

  if (!documentData || typeof documentData !== 'object') {
    return (
      <div className="flex-1 w-full flex items-center justify-center" style={{ background: 'var(--color-surface)', minHeight: '100vh' }}>
        <div className="empty-state">
          <div className="empty-state-title">議事録が見つかりません</div>
          <button
            onClick={() => navigate('/documents')}
            className="btn btn-primary"
            style={{ marginTop: 'var(--space-4)' }}
          >
            一覧に戻る
          </button>
        </div>
      </div>
    )
  }

  // 議事録ヘッダー（タイトル/ステータス/作成日/作成者/フォルダ/共有/削除）。
  // 独立カードを廃止し、各タブのカード上部にまとめて表示する。
  const renderDocHeaderBar = () => (
            <div className="flex items-center gap-3 flex-wrap" style={{ flex: '1 1 auto', minWidth: 0, justifyContent: 'flex-end' }}>
              {/* タイトルとステータス */}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap text-xs text-secondary">
                  {/* botステータス（進行中・失敗時のみ表示） */}
                  {(() => {
                    const bs = documentData?.botStatus as string | undefined
                    if (!bs || bs === 'completed') return null
                    const map: Record<string, { className: string; label: string; pulse?: boolean }> = {
                      scheduled: { className: 'badge', label: 'Bot参加待ち' },
                      waiting: { className: 'badge', label: 'Bot参加待ち' },
                      in_call: { className: 'badge badge-warning', label: 'Bot録音中', pulse: true },
                      in_progress: { className: 'badge badge-warning', label: 'Bot録音中', pulse: true },
                      processing: { className: 'badge badge-info', label: '文字起こし処理中' },
                      failed: { className: 'badge badge-danger', label: 'Bot失敗' },
                      bot_failed: { className: 'badge badge-danger', label: 'Bot失敗' }
                    }
                    const b = map[bs]
                    if (!b) return null
                    return (
                      <span className={b.className}>
                        {b.pulse && (
                          <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: 'currentColor', marginRight: 4, animation: 'bp-bot-pulse 1.2s ease-in-out infinite' }} />
                        )}
                        {b.label}
                      </span>
                    )
                  })()}
                  {isDateEditing ? (
                    <div className="flex items-center gap-2">
                      <span>作成日:</span>
                      <input
                        type="date"
                        value={editedDate}
                        onChange={(e) => setEditedDate(e.target.value)}
                        onBlur={async () => {
                          if (editedDate && documentData) {
                            try {
                              const token = await firebaseUser.getIdToken()
                              const tokenResult = await firebaseUser.getIdTokenResult()
                              const orgId = tokenResult.claims.orgId
                              
                              await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${id}`, {
                                method: 'PATCH',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  audit: {
                                    ...documentData.audit,
                                    createdAt: new Date(editedDate).toISOString()
                                  }
                                })
                              })
                              
                              queryClient.invalidateQueries({ queryKey: ['document', id] })
                            } catch (error) {
                              console.error('Failed to update date:', error)
                            }
                          }
                          setIsDateEditing(false)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur()
                          }
                        }}
                        className="form-input text-xs"
                        style={{ width: 'auto', padding: '4px 8px' }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <span
                      onClick={() => {
                        const currentDate = (() => {
                          if (documentData.audit?.createdAt) {
                            const createdAt = documentData.audit.createdAt
                            if (createdAt.seconds) {
                              return new Date(createdAt.seconds * 1000)
                            }
                            if (typeof createdAt === 'string' || createdAt instanceof Date) {
                              return new Date(createdAt)
                            }
                          }
                          return new Date()
                        })()
                        setEditedDate(currentDate.toISOString().split('T')[0])
                        setIsDateEditing(true)
                      }}
                      className="cursor-pointer px-2 py-1"
                      style={{ borderRadius: 'var(--radius-sm)' }}
                      title="クリックして編集"
                    >
                      作成日: {(() => {
                        if (documentData.audit?.createdAt) {
                          const createdAt = documentData.audit.createdAt
                          // Firebaseのタイムスタンプ形式の場合
                          if (createdAt.seconds) {
                            return new Date(createdAt.seconds * 1000).toLocaleDateString('ja-JP')
                          }
                          // 通常のDate文字列の場合
                          if (typeof createdAt === 'string' || createdAt instanceof Date) {
                            return new Date(createdAt).toLocaleDateString('ja-JP')
                          }
                        }
                        return new Date().toLocaleDateString('ja-JP')
                      })()}
                    </span>
                  )}

                  {/* 作成者 */}
                  {(documentData?.createdByInfo?.name || documentData?.createdByInfo?.email) && (
                    <span className="px-2 py-1 text-secondary" title={documentData.createdByInfo.email || ''}>
                      作成者: {documentData.createdByInfo.name || documentData.createdByInfo.email}
                    </span>
                  )}

                  {/* フォルダ情報・設定 */}
                  <div ref={folderDropdownRef} className="relative flex items-center gap-2">
                    {isFolderEditing && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50 }}>
                        {/* Notionライクなドロップダウン（カードを押し広げないようオーバーレイ表示） */}
                        <div className="dropdown max-h-64 overflow-y-auto" style={{ position: 'static', minWidth: 192 }}>
                          {/* フォルダなしオプション */}
                          <div
                            className="dropdown-option"
                            onClick={() => handleFolderAutoSave(null)}
                          >
                            <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--color-border-subtle)' }} />
                            <span className="text-sm">フォルダなし</span>
                          </div>
                          
                          {/* フォルダオプション */}
                          {folders.map((folder) => (
                            <div
                              key={folder.id}
                              className="dropdown-option"
                              onClick={() => handleFolderAutoSave(folder.id)}
                            >
                              <div
                                className="w-3 h-3 rounded-sm flex-shrink-0"
                                style={{ backgroundColor: folder.color || 'var(--color-accent)' }}
                              />
                              <span className="text-sm truncate">
                                {folder.name}
                              </span>
                              <span className="text-xs text-tertiary ml-auto">
                                {folder.documentCount}件
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                        onClick={() => setIsFolderEditing((v) => !v)}
                        className="btn btn-ghost btn-sm"
                        title="フォルダを変更"
                      >
                        {(() => {
                          // フォルダ一覧から最新の情報を取得
                          const currentFolder = folders.find(f => f.id === documentData?.folderId) || documentData?.folder;
                          if (currentFolder) {
                            return (
                              <>
                                <div 
                                  className="w-3 h-3 rounded-sm flex-shrink-0"
                                  style={{ backgroundColor: currentFolder.color || 'var(--color-accent)' }}
                                />
                                <span className="text-sm">{currentFolder.name}</span>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: 'var(--color-border-subtle)' }} />
                                <span className="text-sm text-tertiary">フォルダなし</span>
                              </>
                            );
                          }
                        })()}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    {/* ブックマーク */}
                    <button
                      onClick={handleToggleBookmark}
                      className="btn btn-ghost btn-sm"
                      title={isBookmarked ? 'ブックマークを解除' : 'ブックマークに追加'}
                      style={isBookmarked ? { color: 'var(--color-warning-mid)' } : undefined}
                    >
                      <svg className="w-4 h-4" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* 共有ステータス、共有ボタン、削除ボタン */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* 共有ステータス - より分かりやすく */}
                {(() => {
                  // documentData.sharedプロパティから共有状態を判定
                  const isShared = (documentData as any)?.shared
                  const acl = documentData?.acl
                  
                  // 共有タイプを判定
                  let shareStatus = null
                  
                  if (isShared) {
                    // 共有されている場合は、共有中として表示
                    shareStatus = {
                      icon: '🔗',
                      label: '共有中',
                      badgeClass: 'badge-info'
                    }
                  } else if (acl && (acl.owners?.length > 1 || acl.editors?.length > 0 || acl.viewers?.length > 0)) {
                    // ACLで複数人に権限がある場合
                    const totalUsers = (acl.owners?.length || 0) + (acl.editors?.length || 0) + (acl.viewers?.length || 0)
                    shareStatus = {
                      icon: '👥',
                      label: `${totalUsers}人がアクセス可能`,
                      badgeClass: 'badge-neutral'
                    }
                  } else {
                    // 非公開（自分のみ）: ボタンと紛らわしいので背景なしのプレーン表示
                    shareStatus = {
                      icon: '🔒',
                      label: '非公開（自分のみ）',
                      badgeClass: ''
                    }
                  }

                  return shareStatus ? (
                    <span className={shareStatus.badgeClass ? `badge ${shareStatus.badgeClass}` : 'flex items-center gap-1 text-sm text-secondary'}>
                      <span>{shareStatus.icon}</span>
                      <span>{shareStatus.label}</span>
                    </span>
                  ) : null
                })()}

                {/* エクスポート（Markdown/テキスト） */}
                <ExportButton documentId={id} />

                {/* 共有ボタン */}
                <button
                  onClick={() => setShowShareDialog(true)}
                  className="btn btn-outline btn-sm"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
                  </svg>
                  共有
                </button>

                {/* 削除ボタン */}
                <button
                  onClick={() => setShowDeleteDialog(true)}
                  className="btn btn-danger btn-sm"
                  title="議事録を削除"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
                  </svg>
                  削除
                </button>
              </div>
            </div>
  )

  // 参加者 + 関連するやりとり（右サイドカラムに表示）
  const renderParticipantsCard = () => (
        <div className="flex flex-col" style={{ width: '100%', minWidth: 0 }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">参加者</h3>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setIsEditingParticipants(!isEditingParticipants)}
            >
              {isEditingParticipants ? '完了' : participants.length === 0 ? '参加者を追加' : '編集'}
            </button>
          </div>
          {participants.length === 0 && !isEditingParticipants && (
            <p className="text-xs text-tertiary" style={{ margin: 0 }}>
              参加者が未登録です。「参加者を追加」から登録すると人物ページに履歴が紐づきます。
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {participants.map((p: string) => (
              <span key={p} className="flex items-center" style={{
                border: '1px solid var(--color-border-light, #e5e7eb)',
                borderRadius: 999,
                padding: '4px 8px 4px 4px'
              }}>
                <Link
                  to={`/people/${encodeURIComponent(p)}`}
                  className="flex items-center gap-2"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  title="人物プロフィールを表示"
                >
                  <span
                    className="avatar avatar-sm"
                    style={{ backgroundColor: avatarColor(p), color: '#fff', border: 'none' }}
                  >
                    {avatarInitial(p)}
                  </span>
                  <span className="text-sm truncate" style={{ maxWidth: 200 }}>{p}</span>
                </Link>
                {isEditingParticipants && (
                  <button
                    onClick={() => removeParticipant(p)}
                    className="bg-transparent border-0 cursor-pointer text-secondary hover:text-danger"
                    style={{ marginLeft: 6, fontSize: 14, lineHeight: 1 }}
                    title="この参加者を削除"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {isEditingParticipants && (
              <span className="flex items-center gap-1">
                <input
                  type="text"
                  value={newParticipantName}
                  onChange={(e) => setNewParticipantName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addParticipant() }}
                  placeholder="参加者を追加"
                  className="form-input"
                  style={{ height: 32, width: 160, fontSize: 13 }}
                />
                <button className="btn btn-outline btn-sm" onClick={addParticipant} disabled={!newParticipantName.trim()}>
                  追加
                </button>
              </span>
            )}
          </div>

          {firstExternalEmail && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-secondary mb-2">
                関連するやりとり（{firstExternalEmail}）
              </h4>
              {participantContextLoading ? (
                <div className="flex items-center gap-2">
                  <span className="spinner sm" />
                  <span className="text-xs text-secondary">読み込み中...</span>
                </div>
              ) : !participantContext || (participantContext.conversations || []).length === 0 ? (
                <span className="text-xs text-secondary">最近のやりとりはありません</span>
              ) : (
                <div className="flex flex-col gap-1">
                  {(participantContext.conversations || []).slice(0, 5).map((c: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 min-w-0">
                      <span style={{ fontSize: 13 }}>{c.type === 'email' ? '📧' : '📅'}</span>
                      {c.type === 'meeting' && c.documentId ? (
                        <Link to={`/documents/${c.documentId}`} className="text-sm truncate" style={{ flex: 1 }}>
                          {c.title || '会議'}
                        </Link>
                      ) : (
                        <span className="text-sm truncate" style={{ flex: 1 }}>
                          {c.type === 'email' ? c.subject || '（件名なし）' : c.title || '会議'}
                        </span>
                      )}
                      <span className="text-xs text-secondary shrink-0">
                        {c.date ? new Date(c.date).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' }) : ''}
                      </span>
                    </div>
                  ))}
                  <Link
                    to={`/people/${encodeURIComponent(firstExternalEmail)}`}
                    className="text-xs"
                    style={{ marginTop: 4 }}
                  >
                    もっと見る →
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
  )

  return (
    <Layout
      title={isTitleEditing ? editedTitle : documentData?.title}
      onTitleSave={handleSaveTitle}
      breadcrumbs={[
        { label: '議事録', to: '/documents' },
        ...(() => {
          const currentFolder = folders.find(f => f.id === documentData?.folderId) || documentData?.folder
          return currentFolder ? [{ label: currentFolder.name, to: `/folder/${currentFolder.id}` }] : []
        })()
      ]}
      showBackButton={true}
      backPath="/"
      showSidebar={true}
      selectedFolderId={selectedFolderId}
      onFolderSelect={handleFolderSelect}
      onFolderCreate={handleFolderCreate}
    >


      {/* タブナビゲーション + ドキュメント情報（作成日/作成者/フォルダ/共有/削除） */}
      <div className="flex items-center justify-between gap-4 flex-wrap" style={{ marginBottom: 'var(--space-4)' }}>
      <div className="tab-list" style={{ marginBottom: 0 }}>
        {[
          { id: 'format', label: '議事録', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
          { id: 'transcribe', label: '文字起こし/音声ファイル', icon: 'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id as any)
              const next = new URLSearchParams(searchParams)
              next.set('tab', tab.id)
              setSearchParams(next, { replace: true })
            }}
            className={`tab-item${activeTab === tab.id ? ' active' : ''}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ display: 'inline', verticalAlign: '-2px', marginRight: 6 }}>
              <path d={tab.icon} />
            </svg>
            {tab.label}
          </button>
        ))}
      </div>
      {renderDocHeaderBar()}
      </div>

      {/* コンテンツエリア */}
      <div>

        {activeTab === 'transcribe' && (
          <div className="transcript-layout">
          <div className="flex flex-col gap-4" style={{ minWidth: 0 }}>
          {/* 参加者カード */}
          <div className="card" style={{ padding: 'var(--space-4) var(--space-5)', marginBottom: 0 }}>
            {renderParticipantsCard()}
          </div>
          <div className="card">
            <div>
              <div className="card-header">
                <h2 className="text-lg font-semibold text-primary">文字起こし/音声ファイル</h2>
                
                {/* 再成形ボタン */}
                <div className="flex items-center gap-3">
                  {/* 保存状態インジケーター */}
                  <div className="flex items-center gap-2 text-sm">
                    {transcriptAutoSave.isSaving ? (
                      <>
                        <span className="spinner sm" />
                        <span className="text-accent">保存中...</span>
                      </>
                    ) : transcriptAutoSave.hasUnsavedChanges ? (
                      <>
                        <div className="w-2 h-2 rounded-full" style={{ background: 'var(--color-warning-mid)' }}></div>
                        <span className="text-warning">未保存の変更</span>
                      </>
                    ) : transcriptAutoSave.lastSaved ? (
                      <>
                        <div className="w-2 h-2 rounded-full" style={{ background: 'var(--color-success-mid)' }}></div>
                        <span className="text-success">保存済み</span>
                      </>
                    ) : null}
                  </div>

                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(currentTranscriptText)
                        setTranscriptCopied(true)
                        setTimeout(() => setTranscriptCopied(false), 2000)
                      } catch {
                        // clipboard unavailable
                      }
                    }}
                    className="btn btn-ghost btn-sm"
                    title="文字起こしテキストをコピー"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    {transcriptCopied ? 'コピーしました' : 'コピー'}
                  </button>

                  <button
                    onClick={() => reformat(currentTranscriptText)}
                    disabled={isReformatting || !currentTranscriptText.trim() || currentTranscriptText === originalTranscriptText}
                    className="btn btn-primary btn-sm"
                    style={
                      currentTranscriptText !== originalTranscriptText && currentTranscriptText.trim()
                        ? undefined
                        : { opacity: 0.5, cursor: 'not-allowed' }
                    }
                  >
                    {isReformatting ? (
                      <>
                        <span className="spinner sm" style={{ borderTopColor: 'currentColor' }} />
                        再成形中...
                      </>
                    ) : (
                      '議事録を再作成'
                    )}
                  </button>
                </div>
              </div>
              
              {/* エラーメッセージ */}
              {(transcriptionError || reformatError || transcriptAutoSave.saveError) && (
                <div className="alert danger mb-4">
                  <div className="alert-body">
                    <p className="text-sm">
                      {transcriptionError || reformatError || transcriptAutoSave.saveError}
                    </p>
                  </div>
                </div>
              )}

              {/* 成功メッセージ */}
              {saveMessage && (
                <div className="alert success mb-4">
                  <div className="alert-body">
                    <p className="text-sm">{saveMessage}</p>
                  </div>
                </div>
              )}

              {/* アップロード/録音メディアの再生（倍速対応）。
                  文字起こし前・失敗時でも再生できるよう transcript の有無に依存させない */}
              {documentData?.audioFile?.path && (
                <div className="alert success mb-4">
                  <div className="alert-body">
                    <div className="alert-title">
                      {isVideoMedia(documentData.audioFile.mimeType, documentData.audioFile.filename) ? '動画ファイル' : '音声ファイル'}
                      {documentData.transcript?.durationSec > 0 && (
                        <span className="text-sm text-secondary" style={{ marginLeft: 8, fontWeight: 400 }}>
                          ({Math.floor(documentData.transcript.durationSec / 60)}:{String(Math.round(documentData.transcript.durationSec) % 60).padStart(2, '0')})
                        </span>
                      )}
                    </div>
                    <p className="text-sm mb-3">
                      {documentData.transcript?.source === 'realtime'
                        ? 'この議事録はリアルタイム音声入力で作成されました。録音された音声を再生できます。'
                        : 'アップロードされたファイルを再生できます。'}
                    </p>
                    <AudioPlayerCard
                      path={documentData.audioFile.path}
                      filename={documentData.audioFile.filename}
                      mimeType={documentData.audioFile.mimeType}
                    />
                  </div>
                </div>
              )}

              {/* 文字起こし情報 */}
              {documentData?.transcript && (
                <div className="mb-4 space-y-4">
                  <div className="alert info">
                    <div className="alert-body">
                      <div className="alert-title">直接編集可能</div>
                      <p className="text-sm">
                        テキストを編集すると自動保存されます。変更後に「議事録を再成形」ボタンで新しい議事録を生成できます。
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notion風のリッチテキストエディター / 話者ターン表示 */}
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-md font-semibold text-primary">文字起こしテキスト（編集可能）</h3>
                  {hasSpeakerTurns(currentTranscriptText) && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className={`btn btn-sm ${transcriptViewMode === 'speakers' ? 'btn-outline' : 'btn-ghost'}`}
                        onClick={() => setTranscriptViewMode('speakers')}
                      >
                        話者別表示
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${transcriptViewMode === 'edit' ? 'btn-outline' : 'btn-ghost'}`}
                        onClick={() => setTranscriptViewMode('edit')}
                      >
                        テキスト編集
                      </button>
                    </div>
                  )}
                </div>
                {/* 話者ラベル付き文字起こしの読み取り専用表示（編集は生テキストのまま） */}
                {hasSpeakerTurns(currentTranscriptText) && transcriptViewMode === 'speakers' ? (
                  <SpeakerTranscript text={currentTranscriptText} />
                ) : (
                <div>
                  <RichTextEditor
                    value={currentTranscriptText}
                    onChange={(newText) => {
                      setCurrentTranscriptText(newText)
                      transcriptAutoSave.scheduleAutoSave(newText)
                    }}
                    onSave={() => transcriptAutoSave.forceSave(currentTranscriptText)}
                    placeholder="文字起こしテキストを入力または編集してください...

会議の内容をここに入力してください。
- 自動保存されます
- Ctrl+S で手動保存
- Tab でインデント"
                    className="border-0"
                    autoFocus={false}
                  />
                </div>
                )}
              </div>
            </div>
          </div>

          </div>

          {/* サイドパネル：録画 / リカバリ */}
          <aside className="transcript-side">
              {documentData?.recording?.path && (
                <div className="card" style={{ padding: 'var(--space-3)' }}>
                  {recordingUrlError ? (
                    <div className="alert danger">
                      <div className="alert-body">
                        <p className="text-sm">{recordingUrlError}</p>
                      </div>
                    </div>
                  ) : recordingVideoUrl ? (
                    <video
                      controls
                      preload="metadata"
                      style={{ width: '100%', borderRadius: 'var(--radius-sm)', background: '#000' }}
                    >
                      <source src={recordingVideoUrl} type={documentData.recording.mimeType || 'video/mp4'} />
                      お使いのブラウザは動画再生に対応していません。
                    </video>
                  ) : (
                    <div className="flex items-center gap-2 py-4">
                      <span className="spinner sm" />
                      <span className="text-secondary text-sm">録画を読み込み中...</span>
                    </div>
                  )}
                  {recordingVideoUrl && (
                    <div className="flex items-center justify-end" style={{ marginTop: 8 }}>
                      <a
                        href={recordingVideoUrl}
                        download={`${documentData?.title || 'recording'}.mp4`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost btn-sm"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                        </svg>
                        ダウンロード
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* botの録画から文字起こしをやり直す（文字起こしが空、または話者分離されていない場合） */}
              {documentData?.integration?.externalId && (!documentData?.transcript?.text || !hasSpeakerTurns(currentTranscriptText)) && (
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                  <h3 className="text-md font-semibold text-primary" style={{ marginBottom: 8 }}>文字起こしのリカバリ</h3>
                  <p className="text-sm text-secondary" style={{ marginBottom: 12 }}>
                    {documentData?.transcript?.text
                      ? '現在の文字起こしは話者が分かれていません。話者分離つきで再取得します（現在のテキストは上書きされます）。'
                      : 'この会議の録画から文字起こしをやり直します。数分かかる場合があります。完了後はAI整形も自動実行されます。'}
                  </p>
                  <button
                    className="btn btn-primary btn-sm w-full"
                    onClick={handleRetranscribeBot}
                    disabled={isRetranscribing}
                  >
                    {isRetranscribing ? '文字起こし中...' : '文字起こしをやり直す'}
                  </button>
                </div>
              )}
          </aside>
          </div>
        )}

        {activeTab === 'format' && documentData?.formatted && (
          <div className="transcript-layout">
          <div className="flex flex-col gap-4" style={{ minWidth: 0 }}>
          {/* 参加者カード */}
          <div className="card" style={{ padding: 'var(--space-4) var(--space-5)', marginBottom: 0 }}>
            {renderParticipantsCard()}
          </div>
          <div className="card">
            <div>
              <div className="card-header">
                <h2 className="text-lg font-semibold text-primary">議事録</h2>
                
                <div className="flex items-center gap-4">
                  {/* 保存状態インジケーター（編集モード時のみ） */}
                  {isFormattedEditing && (
                    <div className="flex items-center gap-2 text-sm">
                      {formattedAutoSave.isSaving ? (
                        <>
                          <span className="spinner sm" />
                          <span className="text-accent">保存中...</span>
                        </>
                      ) : formattedAutoSave.hasUnsavedChanges ? (
                        <>
                          <div className="w-2 h-2 rounded-full" style={{ background: 'var(--color-warning-mid)' }}></div>
                          <span className="text-warning">未保存の変更</span>
                        </>
                      ) : formattedAutoSave.lastSaved ? (
                        <>
                          <div className="w-2 h-2 rounded-full" style={{ background: 'var(--color-success-mid)' }}></div>
                          <span className="text-success">保存済み</span>
                        </>
                      ) : null}
                    </div>
                  )}

                  {/* アクションボタン */}
                  <div className="flex items-center gap-2">
                    {/* Markdownコピーボタン */}
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(currentFormattedText)
                          setMarkdownCopied(true)
                          setTimeout(() => setMarkdownCopied(false), 2000)
                        } catch {
                          // clipboard unavailable
                        }
                      }}
                      className="btn btn-ghost btn-sm"
                      title="議事録のMarkdownテキストをコピー"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      {markdownCopied ? 'コピーしました' : 'コピー'}
                    </button>

                    {/* 再整形ボタン */}
                    <button
                      onClick={() => handleFormatDocument(true)}
                      disabled={isFormatting}
                      className="btn btn-outline btn-sm"
                      style={isFormatting ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    >
                      {isFormatting ? (
                        <>
                          <span className="spinner sm" />
                          再整形中...
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 12a9 9 0 0 1-15 6.7L3 16" />
                            <path d="M21 3v5h-5M3 21v-5h5" />
                          </svg>
                          再作成
                        </>
                      )}
                    </button>

                    {/* モード切り替えボタン */}
                    <button
                      onClick={() => setIsFormattedEditing(!isFormattedEditing)}
                      className={`btn btn-sm ${isFormattedEditing ? 'btn-outline' : 'btn-ghost'}`}
                    >
                      {isFormattedEditing ? (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          表示モード
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5m-1.414-9.414a2 2 0 1 1 2.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          編集モード
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* エラーメッセージ */}
              {formattedAutoSave.saveError && (
                <div className="alert danger mb-4">
                  <div className="alert-body">
                    <p className="text-sm">{formattedAutoSave.saveError}</p>
                  </div>
                </div>
              )}

              {/* 表示モード */}
              {!isFormattedEditing && (
                <MarkdownRenderer
                  content={currentFormattedText}
                  onEdit={() => setIsFormattedEditing(true)}
                  showEditButton={true}
                  className="min-h-[400px] max-w-4xl mx-auto"
                />
              )}

              {/* 編集モード */}
              {isFormattedEditing && (
                <div>
                  {/* Markdownエディター */}
                  <MarkdownEditor
                    value={currentFormattedText}
                    onChange={(newText) => {
                      setCurrentFormattedText(newText)
                      formattedAutoSave.scheduleAutoSave(newText)
                    }}
                    onSave={() => formattedAutoSave.forceSave(currentFormattedText)}
                    placeholder="議事録をMarkdownで編集してください...

# 議事録タイトル

## 参加者
- 参加者1
- 参加者2

## 議題
1. 議題1
2. 議題2

## 決定事項
- 決定事項1
- 決定事項2

## アクションアイテム
- [ ] タスク1 (担当者: xxx)
- [ ] タスク2 (担当者: yyy)"
                    showPreview={true}
                  />
                </div>
              )}
            </div>
          </div>

          </div>

          {/* 右カラム: 検出されたアクションアイテム + 前回・関連MTG */}
          <aside className="transcript-side">
            {id && (
              <ActionItemTaskify
                documentId={id}
                markdown={currentFormattedText}
                participants={participants}
              />
            )}
            {id && <RelatedMeetings documentId={id} />}
          </aside>
          </div>
        )}

        {activeTab === 'format' && !documentData?.formatted && (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6M9 13h6M9 17h6" />
                </svg>
              </div>
              <h3 className="empty-state-title">AI整形未実行</h3>
              <p className="empty-state-desc">文字起こしからAI整形された議事録を生成します</p>
              <button
                onClick={() => handleFormatDocument()}
                disabled={isFormatting}
                className="btn btn-primary"
                style={{ marginTop: 'var(--space-4)', ...(isFormatting ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
              >
                {isFormatting ? (
                  <>
                    <span className="spinner sm" style={{ borderTopColor: 'currentColor' }} />
                    AI整形中...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    AI整形を開始
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* デバッグ情報（管理者のみ） */}
        {user?.role === 'admin' && activeTab === 'format' && (
          <div className="card">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-4">デバッグ情報（管理者専用）</h2>

              {/* デバッグ情報 */}
              <details className="mb-4 p-3 text-xs" style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-sm)' }}>
                <summary className="cursor-pointer text-secondary">ドキュメントデータ (デバッグ情報)</summary>
                <pre className="mt-2 text-secondary overflow-auto">
                  {JSON.stringify({
                    hasTranscript: !!documentData?.transcript,
                    transcriptType: typeof documentData?.transcript,
                    hasTranscriptText: !!(documentData?.transcript && documentData.transcript.text),
                    transcriptTextLength: documentData?.transcript?.text?.length || 0,
                    transcript: documentData?.transcript,
                    hasFormatted: !!documentData?.formatted,
                    formattedType: typeof documentData?.formatted,
                    hasMarkdown: !!(documentData?.formatted && documentData.formatted.markdown),
                    markdownLength: documentData?.formatted?.markdown?.length || 0,
                    formatted: documentData?.formatted
                  }, null, 2)}
                </pre>
              </details>

              {documentData?.formatted && (
                <details className="p-3 text-xs" style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-sm)' }}>
                  <summary className="cursor-pointer text-secondary">整形データ詳細</summary>
                  <pre className="mt-2 text-secondary overflow-auto">
                    {JSON.stringify(documentData.formatted, null, 2)}
                  </pre>
                </details>
              )}

              {documentData?.formatted && documentData.formatted.status === 'failed' && (
                <div className="empty-state">
                  <div className="empty-state-icon icon-wrap-danger">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 9v4M12 17h.01" />
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    </svg>
                  </div>
                  <h3 className="empty-state-title">AI整形を再試行</h3>
                  <p className="empty-state-desc">
                    前回の処理に失敗しました。しばらく時間をおいてから再試行してください。
                  </p>
                  <button
                    onClick={() => handleFormatDocument(true)}
                    disabled={isFormatting}
                    className="btn btn-primary"
                    style={{ marginTop: 'var(--space-4)', ...(isFormatting ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                  >
                    {isFormatting ? (
                      <>
                        <span className="spinner sm" style={{ borderTopColor: 'currentColor' }} />
                        AI整形中...
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 12a9 9 0 0 1-15 6.7L3 16" />
                          <path d="M21 3v5h-5M3 21v-5h5" />
                        </svg>
                        AI整形を再実行
                      </>
                    )}
                  </button>
                  {!isFormatting && (formatError || retryMessage) && (
                    <div className="mt-2 w-full">
                      {retryMessage && retryMessage.includes('自動で再試行') ? (
                        <div className="alert warning compact">
                          <span className="spinner sm alert-icon" />
                          <div className="alert-body">
                            <p className="text-sm font-medium">{retryMessage}</p>
                          </div>
                        </div>
                      ) : retryMessage && (retryMessage.includes('AI整形を開始しています') || retryMessage.includes('処理中')) ? (
                        <div className="alert info compact">
                          <span className="spinner sm alert-icon" />
                          <div className="alert-body">
                            <p className="text-sm font-medium">{retryMessage}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-danger text-sm">{formatError || retryMessage}</p>
                      )}
                    </div>
                  )}

                  {/* 処理中の表示 */}
                  {isFormatting && (
                    <div className="mt-4 w-full">
                      <div className="alert info">
                        <div className="alert-body">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="spinner sm" />
                              <span className="font-semibold text-primary">AI整形処理中...</span>
                            </div>
                            {formattingEstimatedTime && (
                              <span className="text-sm text-secondary font-medium">
                                完了予定: {formattingEstimatedTime}
                              </span>
                            )}
                          </div>

                          {/* 進捗バー */}
                          <div className="mb-2">
                            <div className="flex justify-between text-xs text-secondary mb-1">
                              <span>進捗状況</span>
                              <span>{formattingProgress}%</span>
                            </div>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${formattingProgress}%` }}
                              ></div>
                            </div>
                          </div>

                          {retryMessage && (
                            <p className="text-sm text-secondary mt-2">
                              {retryMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {documentData?.formatted && documentData.formatted.status === 'processing' && !isStaleProcessing && (
                <div className="empty-state">
                  <div className="alert info mb-6 w-full">
                    <span className="spinner sm alert-icon" />
                    <div className="alert-body">
                      <div className="alert-title">AI整形処理中...</div>
                      <p className="text-sm">
                        Claude AIが議事録を整形しています。しばらくお待ちください。
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      console.log('🔄 Manual refresh requested...')
                      await queryClient.invalidateQueries({ queryKey: ['document', id] })
                      const freshData = await fetchDocument(id!, firebaseUser)
                      queryClient.setQueryData(['document', id], freshData)
                      console.log('✅ Manual refresh completed')
                    }}
                    className="btn btn-outline"
                  >
                    状態を更新
                  </button>
                </div>
              )}

              {/* 整形が中断されたまま止まっている場合の再実行 */}
              {documentData?.formatted && documentData.formatted.status === 'processing' && isStaleProcessing && (
                <div className="empty-state">
                  <div className="alert danger mb-6 w-full">
                    <div className="alert-body">
                      <div className="alert-title">AI整形が中断された可能性があります</div>
                      <p className="text-sm">
                        処理が完了していません。再実行してください。
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleFormatDocument(true)}
                    className="btn btn-primary"
                    disabled={isFormattingAPI}
                  >
                    {isFormattingAPI ? 'AI整形中...' : 'AI整形を再実行'}
                  </button>
                </div>
              )}

              {!documentData?.transcript || !documentData.transcript.text && (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8" />
                    </svg>
                  </div>
                  <h3 className="empty-state-title">文字起こしが必要です</h3>
                  <p className="empty-state-desc">
                    AI整形を行うには、まず文字起こしデータが必要です。<br />
                    文字起こしタブで音声ファイルをアップロードしてください。
                  </p>
                  <button
                    onClick={() => setActiveTab('transcribe')}
                    className="btn btn-primary"
                    style={{ marginTop: 'var(--space-4)' }}
                  >
                    文字起こしタブに移動
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* 削除確認ダイアログ */}
      {showDeleteDialog && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ width: 448 }}>
            <div className="modal-header">
              <div className="flex items-center gap-3">
                <span className="icon-wrap icon-wrap-md icon-wrap-danger">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                    <path d="M12 9v4M12 17h.01" />
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  </svg>
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-primary">議事録を削除</h3>
                  <p className="text-sm text-tertiary">この操作は取り消せません。</p>
                </div>
              </div>
            </div>

            <div className="modal-body">
              <p className="text-primary">
                「<strong>{documentData?.title}</strong>」を完全に削除しますか？
              </p>
              <p className="text-sm text-tertiary mt-2">
                議事録の内容、文字起こし、AI整形結果がすべて削除されます。
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
                className="btn btn-outline"
                style={isDeleting ? { opacity: 0.5 } : undefined}
              >
                キャンセル
              </button>
              <button
                onClick={handleDeleteDocument}
                disabled={isDeleting}
                className="btn btn-danger"
                style={isDeleting ? { opacity: 0.5 } : undefined}
              >
                {isDeleting ? (
                  <>
                    <span className="spinner sm" style={{ borderTopColor: 'currentColor' }} />
                    削除中...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
                    </svg>
                    削除する
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 共有ダイアログ */}
      {showShareDialog && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ width: 480 }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold text-primary">議事録を共有</h3>
              <button
                onClick={() => {
                  setShowShareDialog(false)
                  setIsCopied(false)
                }}
                className="close-btn"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
            {/* メンバー検索・追加 */}
            <div className="form-group">
              <MemberSearchAdd
                members={orgMembers}
                excludeUids={aclEntries.map(e => e.uid)}
                onAdd={(m) => setAclEntries(prev => [...prev, { ...m, role: 'viewer' }])}
                placeholder="メンバーを検索して追加"
              />
            </div>

            {/* アクセスできるメンバー */}
            {aclEntries.length > 0 && (
              <div className="space-y-1 mb-4">
                <div className="text-xs font-medium text-tertiary mb-1">アクセスできるメンバー</div>
                {aclEntries.map((entry) => (
                  <div key={entry.uid} className="flex items-center gap-3 py-2">
                    <MemberAvatar name={entry.displayName} email={entry.email} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-primary truncate">
                        {entry.displayName || entry.email}
                        {entry.uid === user?.uid && <span className="text-tertiary">（あなた）</span>}
                      </div>
                      <div className="text-xs text-tertiary truncate">{entry.email}</div>
                    </div>
                    <select
                      value={entry.role}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === 'remove') {
                          setAclEntries(prev => prev.filter(x => x.uid !== entry.uid))
                        } else {
                          setAclEntries(prev => prev.map(x => x.uid === entry.uid ? { ...x, role: value as 'owner' | 'editor' | 'viewer' } : x))
                        }
                      }}
                      className="form-select text-sm"
                      style={{ padding: '4px 8px', width: 'auto' }}
                    >
                      <option value="owner">オーナー</option>
                      <option value="editor">編集者</option>
                      <option value="viewer">閲覧者</option>
                      <option value="remove">削除</option>
                    </select>
                  </div>
                ))}
              </div>
            )}

            {/* 一般的なアクセス（公開範囲） */}
            <div style={{ borderTop: '1px solid var(--color-border-light)', paddingTop: 12 }}>
              <div className="text-xs font-medium text-tertiary mb-1">一般的なアクセス</div>
              <div className="flex items-center gap-3 py-2">
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'var(--color-surface)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  {docVisibility === 'org' ? '🏢' : docVisibility === 'folder' ? '📁' : docVisibility === 'members' ? '👥' : '🔒'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-primary">公開範囲</div>
                  <div className="text-xs text-tertiary">
                    {docVisibility === 'private' && '自分（と上のリストのオーナー・編集者）だけが閲覧できます'}
                    {docVisibility === 'members' && '上のリストに追加したメンバーだけが閲覧できます'}
                    {docVisibility === 'folder' && (
                      documentData?.folderId
                        ? `フォルダ「${folders.find((f: any) => f.id === documentData.folderId)?.name || '不明'}」にアクセスできる人が閲覧できます`
                        : 'フォルダ未設定のため、自分だけが閲覧できます'
                    )}
                    {docVisibility === 'org' && (() => {
                      const currentFolder = folders.find((f: any) => f.id === documentData?.folderId) as any
                      if (currentFolder?.visibility === 'restricted') {
                        return (
                          <span style={{ color: 'var(--color-warning-mid, #d97706)' }}>
                            フォルダ「{currentFolder.name}」が限定メンバー設定のため、実際にはフォルダのメンバーだけが閲覧できます（フォルダ制限が優先されます）
                          </span>
                        )
                      }
                      return '組織のメンバー全員が閲覧できます'
                    })()}
                  </div>
                </div>
                <select
                  value={docVisibility}
                  onChange={(e) => setDocVisibility(e.target.value as 'private' | 'members' | 'folder' | 'org')}
                  className="form-select text-sm"
                  style={{ padding: '4px 8px', width: 'auto' }}
                >
                  <option value="private">自分だけ</option>
                  <option value="members">指定メンバー</option>
                  <option value="folder">フォルダ内</option>
                  <option value="org">組織全体</option>
                </select>
              </div>
            </div>

            {/* リンク共有 */}
            <div style={{ borderTop: '1px solid var(--color-border-light)', paddingTop: 12, marginTop: 8 }}>
              <div className="text-xs font-medium text-tertiary mb-1">リンク共有</div>
              {shareUrl ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="form-input flex-1 text-sm"
                    style={{ background: 'var(--color-surface)' }}
                  />
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(shareUrl)
                        setIsCopied(true)
                        setTimeout(() => setIsCopied(false), 2000)
                      } catch (error) {
                        console.error('Failed to copy:', error)
                      }
                    }}
                    className="btn btn-outline btn-sm"
                  >
                    {isCopied ? 'コピー済み' : 'コピー'}
                  </button>
                  <button
                    onClick={handleRevokeShareLinks}
                    disabled={isLinkBusy}
                    className="btn btn-outline btn-sm"
                    style={{ color: 'var(--color-danger)' }}
                  >
                    解除
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-tertiary">URLを知っている人が閲覧できるリンクを作成します</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={shareLinkExpiry}
                      onChange={(e) => setShareLinkExpiry(e.target.value as typeof shareLinkExpiry)}
                      className="form-select text-sm"
                      style={{ padding: '4px 8px', width: 'auto' }}
                    >
                      <option value="none">有効期限なし</option>
                      <option value="7d">7日間</option>
                      <option value="30d">30日間</option>
                      <option value="90d">90日間</option>
                    </select>
                    <input
                      type="text"
                      className="form-input text-sm"
                      style={{ flex: '1 1 140px', padding: '4px 8px' }}
                      placeholder="パスワード（任意）"
                      value={shareLinkPassword}
                      onChange={(e) => setShareLinkPassword(e.target.value)}
                      autoComplete="off"
                    />
                    <button
                      onClick={handleCreateShareLink}
                      disabled={isLinkBusy}
                      className="btn btn-outline btn-sm"
                      style={{ flexShrink: 0 }}
                    >
                      {isLinkBusy ? '作成中...' : 'リンクを作成'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowShareDialog(false)
                  setIsCopied(false)
                }}
                className="btn btn-outline"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveVisibility}
                disabled={isSavingShare}
                className="btn btn-primary"
              >
                {isSavingShare ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}